/**
 * Time Entry Service
 * Handles all time tracking operations
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Start a new timer
 */
export const startTimer = async (userId, clientId, description = '') => {
  try {
    // Check if there's already a running timer
    const runningTimer = await getRunningTimer(userId);
    if (runningTimer) {
      throw new Error('Another timer is already running');
    }

    // Fetch client to get default rate
    const clientDoc = await getDoc(doc(db, 'clients', clientId));
    if (!clientDoc.exists()) {
      throw new Error('Client not found');
    }

    const client = clientDoc.data();

    const entryData = {
      userId,
      clientId,
      clientName: client.name,
      description,
      startTime: serverTimestamp(),
      endTime: null,
      duration: null,
      rate: client.defaultRate || 0,
      amount: null,
      invoiced: false,
      invoiceId: null,
      isRunning: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, 'timeEntries'), entryData);
    return { entryId: docRef.id, startTime: new Date() };
  } catch (error) {
    console.error('Error starting timer:', error);
    throw error;
  }
};

/**
 * Stop a running timer
 */
export const stopTimer = async (entryId) => {
  try {
    const entryRef = doc(db, 'timeEntries', entryId);
    const entryDoc = await getDoc(entryRef);

    if (!entryDoc.exists()) {
      throw new Error('Timer entry not found');
    }

    const entry = entryDoc.data();
    const endTime = new Date();
    const startTime = entry.startTime.toDate();
    const duration = Math.floor((endTime - startTime) / 1000); // seconds
    const hours = duration / 3600;
    const amount = hours * entry.rate;

    await updateDoc(entryRef, {
      endTime: serverTimestamp(),
      duration,
      amount,
      isRunning: false,
      updatedAt: serverTimestamp(),
    });

    return { duration, amount };
  } catch (error) {
    console.error('Error stopping timer:', error);
    throw error;
  }
};

/**
 * Get currently running timer for user
 */
export const getRunningTimer = async (userId) => {
  try {
    const q = query(
      collection(db, 'timeEntries'),
      where('userId', '==', userId),
      where('isRunning', '==', true)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  } catch (error) {
    console.error('Error getting running timer:', error);
    throw error;
  }
};

/**
 * Create manual time entry
 */
export const createTimeEntry = async (userId, data) => {
  try {
    const { clientId, startTime, endTime, description } = data;

    // Validate times
    if (endTime <= startTime) {
      throw new Error('End time must be after start time');
    }

    // Fetch client
    const clientDoc = await getDoc(doc(db, 'clients', clientId));
    if (!clientDoc.exists()) {
      throw new Error('Client not found');
    }

    const client = clientDoc.data();
    const duration = Math.floor((endTime - startTime) / 1000);
    const hours = duration / 3600;
    const rate = client.defaultRate || 0;
    const amount = hours * rate;

    const entryData = {
      userId,
      clientId,
      clientName: client.name,
      description: description || '',
      startTime,
      endTime,
      duration,
      rate,
      amount,
      invoiced: false,
      invoiceId: null,
      isRunning: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, 'timeEntries'), entryData);
    return { entryId: docRef.id };
  } catch (error) {
    console.error('Error creating time entry:', error);
    throw error;
  }
};

/**
 * Update time entry
 */
export const updateTimeEntry = async (entryId, data) => {
  try {
    const entryRef = doc(db, 'timeEntries', entryId);
    const entryDoc = await getDoc(entryRef);

    if (!entryDoc.exists()) {
      throw new Error('Time entry not found');
    }

    const entry = entryDoc.data();

    if (entry.invoiced) {
      throw new Error('Cannot edit invoiced time entries');
    }

    // Recalculate duration and amount if times changed
    if (data.startTime || data.endTime) {
      const startTime = data.startTime || entry.startTime.toDate();
      const endTime = data.endTime || entry.endTime.toDate();
      const duration = Math.floor((endTime - startTime) / 1000);
      const hours = duration / 3600;
      data.duration = duration;
      data.amount = hours * entry.rate;
    }

    await updateDoc(entryRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating time entry:', error);
    throw error;
  }
};

/**
 * Delete time entry
 */
export const deleteTimeEntry = async (entryId) => {
  try {
    const entryRef = doc(db, 'timeEntries', entryId);
    const entryDoc = await getDoc(entryRef);

    if (!entryDoc.exists()) {
      throw new Error('Time entry not found');
    }

    const entry = entryDoc.data();

    if (entry.invoiced) {
      throw new Error('Cannot delete invoiced time entries');
    }

    await deleteDoc(entryRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting time entry:', error);
    throw error;
  }
};

/**
 * Get time entries for user
 */
export const getTimeEntries = async (userId, filters = {}) => {
  try {
    let q = query(
      collection(db, 'timeEntries'),
      where('userId', '==', userId),
      orderBy('startTime', 'desc')
    );

    // Apply filters
    if (filters.clientId) {
      q = query(q, where('clientId', '==', filters.clientId));
    }

    if (filters.invoiced !== undefined) {
      q = query(q, where('invoiced', '==', filters.invoiced));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting time entries:', error);
    throw error;
  }
};

/**
 * Subscribe to time entries changes
 * @param {string} userId - User ID
 * @param {function} callback - Callback function
 * @param {boolean} isAdmin - If true, returns all entries regardless of userId
 * @param {object} filters - Optional filters
 */
export const subscribeToTimeEntries = (userId, callback, isAdmin = false, filters = {}) => {
  // Admins can see all time entries
  let q = isAdmin
    ? query(collection(db, 'timeEntries'), orderBy('startTime', 'desc'))
    : query(
        collection(db, 'timeEntries'),
        where('userId', '==', userId),
        orderBy('startTime', 'desc')
      );

  if (filters.clientId) {
    q = query(q, where('clientId', '==', filters.clientId));
  }

  return onSnapshot(q, (snapshot) => {
    const entries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(entries);
  });
};
